/**
 * useDeleteGroup Hook
 *
 * React Query mutation hook for deleting a group.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { groupService } from '../../../api/group.service';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * Hook for deleting a group
 */
export const useDeleteGroup = (
  tenantId: string
): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (groupId) => {
      console.log('[useDeleteGroup] Deleting group:', groupId);
      return groupService.deleteGroup(tenantId, groupId);
    },
    onSuccess: (_, groupId) => {
      console.log('[useDeleteGroup] Group deleted successfully:', groupId);
      // Remove from cache
      queryClient.removeQueries({ queryKey: ['group', tenantId, groupId] });
      // Invalidate groups list to refresh the data
      queryClient.invalidateQueries({ queryKey: ['groups', tenantId] });
      // Invalidate users list (group was removed from user.groups arrays)
      queryClient.invalidateQueries({ queryKey: ['users', tenantId] });
    },
    onError: (error) => {
      console.error('[useDeleteGroup] Failed to delete group:', getErrorMessage(error));
    },
  });
};
